<template>
  <page-loader>
    <main>
      <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0 mb-8" />
      <v-toolbar dense flat color="transparent">
        <v-toolbar-title class="headline">
          {{ entity.entity.default.name }}
          <span></span>
          <base-chip :text="entity.entityType.name" icon="label" class="ml-2" small dark color="light-blue" />
        </v-toolbar-title>

        <v-spacer />

        <v-tooltip max-width="300" bottom>
          <template #activator="{ on }">
            <base-chip v-show="hasChanges" color="blue" small dark v-on="on">
              <v-icon small class="mr-1">info</v-icon>
              <span>Changes Detected</span>
            </base-chip>
          </template>
          <span>Remember to save your changes before leaving!</span>
        </v-tooltip>
      </v-toolbar>
      <v-divider />

      <v-container>
        <v-tabs v-model="tab" :disabled="isLoading || isDisabled" background-color="transparent" class="mb-4">
          <v-tab>Core Information</v-tab>
          <v-tab v-if="hasConfiguration">
            Configuration
          </v-tab>
          <v-tab>Pages</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab" class="transparent" continuous>
          <v-tab-item>
            <v-row>
              <v-col>
                <geo-tabs
                  v-model="tabGeo"
                  :countries="countriesPredefined"
                  :is-disabled="!canPerformActions || !isPermittedTo('update')"
                  @add="onAddCountry"
                  @delete="onDeleteCountry"
                />
                <v-card v-if="categorisedSchemaGeoGeneral" class="pa-2" outlined>
                  <v-row v-for="(fields, category) in categorisedSchemaGeoGeneral" :key="category">
                    <v-col>
                      <category-options
                        :category="category"
                        :data="updatedEntityByGeo"
                        :offers="mappedOffers"
                        :fields="fields"
                        :is-disabled="isDisabled || !isPermittedTo('update')"
                        :is-loading="isLoading"
                        @input="onInputGeo"
                      />
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <template v-if="Object.keys(categorisedSchemaGeo.language).length">
              <v-row>
                <v-col>
                  <v-toolbar color="secondary" dark dense flat>
                    <v-toolbar-title class="d-flex align-center">
                      <v-icon class="mr-4">language</v-icon>
                      Language fields
                    </v-toolbar-title>
                    <v-spacer />
                    <v-select
                      v-model="query.params.language"
                      :disabled="isLoading"
                      :items="languageOptions"
                      :loading="isLoading"
                      class="flex-grow-0 context-input"
                      item-text="key"
                      label="Select Language"
                      dense
                      hide-details
                      solo
                    />
                  </v-toolbar>
                </v-col>
              </v-row>
              <v-row>
                <v-col v-for="(category, key) in categorisedSchemaGeo.language" :key="`language-${category.key}`">
                  <category-options
                    :category="key"
                    :context="query.params.language"
                    :data="updatedEntityByGeo"
                    :fields="category"
                    :is-disabled="isDisabled || !isPermittedTo('update')"
                    :is-loading="isLoading"
                    :site-groups="siteGroups"
                    :offers="mappedOffers"
                    @input="onInputGeo"
                  />
                </v-col>
              </v-row>
            </template>
            <template v-if="Object.keys(categorisedSchemaGeo.currency).length">
              <v-row>
                <v-col>
                  <v-toolbar color="secondary" dark dense flat>
                    <v-toolbar-title class="d-flex align-center">
                      <v-icon class="mr-4">attach_money</v-icon>
                      Currency fields
                    </v-toolbar-title>
                    <v-spacer />
                    <v-select
                      v-model="query.params.currency"
                      :disabled="isLoading"
                      :items="options.mainCurrencies"
                      :loading="isLoading"
                      class="flex-grow-0 context-input"
                      item-text="key"
                      label="Select Currency"
                      dense
                      hide-details
                      solo
                    />
                  </v-toolbar>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  v-for="(category, key) in categorisedSchemaGeo.currency"
                  :key="`currency-${category.key}`"
                  sm="6"
                >
                  <category-options
                    :category="key"
                    :context="query.params.currency"
                    :offers="mappedOffers"
                    :data="updatedEntityByGeo"
                    :fields="category"
                    :is-disabled="isDisabled || !isPermittedTo('update')"
                    :is-loading="isLoading"
                    @input="onInputGeo"
                  />
                </v-col>
              </v-row>
            </template>
          </v-tab-item>

          <v-tab-item v-if="hasConfiguration">
            <v-card class="pa-2" outlined>
              <v-row>
                <v-col>
                  <category-options
                    category="Configuration"
                    :data="updatedEntity"
                    :fields="categorisedSchema.general.Configuration"
                    :offers="mappedOffers"
                    :exclude="['entity_type_id']"
                    :is-disabled="isDisabled || !isPermittedTo('update')"
                    :is-loading="isLoading"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-2" outlined>
              <v-row>
                <v-col>
                  <page-resource
                    :filters="{ entity_id: entity.id }"
                    resource-type="entityPages"
                    :exclude="['entities']"
                    :show-edit="false"
                    :show-delete="false"
                    :show-attach="false"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-container>
      <fab-actions
        :can-delete="isPermittedTo('delete')"
        :can-edit="isPermittedTo('update')"
        :has-changes="hasChanges"
        context="Entity"
        @delete="showDeleteModal = true"
        @reset="showResetModal = true"
        @save="showSaveModal = true"
      />
      <delete-modal
        v-model="showDeleteModal"
        :is-disabled="isLoading"
        :is-loading="isLoading"
        :item="entity.entity.default.name"
        category="Entity"
        @modal-confirm="onDeleteConfirm"
      />
      <reset-modal
        v-model="showResetModal"
        :is-disabled="isLoading"
        :is-loading="isLoading"
        category="offer"
        @modal-confirm="onResetConfirm"
      />
      <exit-modal
        :is-disabled="isLoading"
        :is-loading="isLoading"
        :value="showExitModal"
        @modal-confirm="confirmExitBeforeSave"
        @modal-cancel="cancelExitBeforeSave"
      />
      <generic-modal
        v-model="showSaveModal"
        :is-disabled="isLoading"
        :is-loading="isLoading"
        title="Save Entity"
        persistent
        @modal-confirm="onSaveConfirm"
        @modal-cancel="showSaveModal = false"
      >
        <span>Are you sure you want to save these changes?</span>
      </generic-modal>
    </main>
  </page-loader>
</template>

<script>
// Vue
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
// Services
import { entities, languages, offers, siteGroups } from '@/services/offers'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Helpers
import { cloneDeep, merge } from 'lodash-es'
import { categoriseSchema, parseSchema, getGeneralInformationModels } from '@/utils/parse-schema'
// Components
import BaseChip from '@/components/chips/base-chip'
import CategoryOptions from '@/components/category-options'
import DeleteModal from '@/components/modals/delete-modal'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import GeoTabs from '@/components/geo-tabs'
import PageLoader from '@/components/page-loader'
import PageResource from '@/components/resources/page-resource'
import ResetModal from '@/components/modals/reset-modal'

const blankEntity = () => ({
  entity: {
    default: {
      name: '',
    },
  },
  entityType: {
    name: '',
  },
})

export default {
  name: 'EntitiesEdit',
  components: {
    BaseChip,
    CategoryOptions,
    DeleteModal,
    FabActions,
    GenericModal,
    GeoTabs,
    PageResource,
    PageLoader,
    ResetModal,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  data() {
    return {
      entity: blankEntity(),
      isDisabled: false,
      isLoading: false,
      languageOptions: [],
      offers: [],
      siteGroups: null,
      parsedSchema: {},
      parsedSchemaGeo: {},
      queryDefaults: ['currency', 'language'],
      queriesDefined: ['currency', 'language'],
      showDeleteModal: false,
      showResetModal: false,
      showSaveModal: false,
      tab: 0,
      tabGeo: 0,
      updatedEntity: blankEntity(),
    }
  },
  computed: {
    ...mapGetters('options', ['getItemsForOption', 'options']),
    ...mapState('entities', ['schema']),
    mappedOffers() {
      return this.offers.map(({ slug, type, name }) => ({
        key: `${name || slug} (${this.formattedOption('offerTypes', type)})`,
        value: slug,
      }))
    },
    breadcrumbs() {
      return [
        { text: 'Entities', to: { name: 'entities.management' }, exact: true },
        { text: this.entity?.entity?.default?.name || 'loading', disabled: true },
      ]
    },
    categorisedSchema() {
      if (!Object.keys(this.parsedSchema).length) return { currency: {}, general: {}, language: {} }
      return categoriseSchema(this.parsedSchema)
    },
    categorisedSchemaGeo() {
      if (!Object.keys(this.parsedSchemaGeo).length) return { currency: {}, general: {}, language: {} }
      return categoriseSchema(this.parsedSchemaGeo)
    },
    categorisedSchemaGeoGeneral() {
      const ignore = ['Configuration', 'Imagary']
      return Object.entries(this.categorisedSchemaGeo.general).reduce((acc, [key, data]) => {
        return ignore.includes(key) ? acc : { ...acc, [key]: data }
      }, {})
    },
    countriesPredefined() {
      if (!this.updatedEntity.entity) return []

      return Object.entries(this.updatedEntity.entity)
        .filter(([, values]) => {
          const onlyHasPosition = Object.entries(values).length === 1 && values.position !== undefined
          return !onlyHasPosition
        })
        .map(([country]) => country)
        .sort(country => (country === 'default' ? -1 : 0))
    },
    currentCountry() {
      return this.countriesPredefined[this.tabGeo]
    },
    hasChanges() {
      return this.hasDataChanged(this.entity, this.updatedEntity)
    },
    hasConfiguration() {
      return this.categorisedSchema && this.categorisedSchema.general && this.categorisedSchema.general.Configuration
    },
    updatedEntityByGeo() {
      return this.updatedEntity.entity && this.updatedEntity.entity[this.currentCountry]
    },
  },
  async created() {
    const [languageData, { data: offerData }, { data: siteGroupData }] = await Promise.all([
      languages.getLanguages(),
      offers.getAllOffers({ compact: true }),
      siteGroups.getAll(),
    ])

    this.languageOptions = languageData.map(({ name, code }) => ({ key: name, value: code }))
    this.offers = offerData
    this.siteGroups = siteGroupData

    await this.getEntity()
    this.syncUpdatedEntity()

    const { type } = this.entity.entityType
    await this.getEntitySchema(type)

    this.parsedSchema = parseSchema({ ...this.schema[type], patternProperties: {} })
    this.parsedSchemaGeo = parseSchema(this.schema[type].patternProperties.default)

    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    ...mapActions('entities', ['getEntitySchema']),
    async deleteEntity() {
      this.isLoading = true

      try {
        await entities.deleteEntity(this.$route.params.id)
        this.$router.push({ name: 'entities' })
        this.$notify({
          type: 'success',
          text: `Deleted ${this.entity.entity.default.name} entity successfully`,
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete entity',
        })
      }

      this.isLoading = false
    },
    async getEntity() {
      this.isLoading = true

      try {
        this.entity = await entities.getEntity(this.$route.params.id)
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch Entity data.',
        })
      }

      this.isLoading = false
    },
    onAddCountry(country) {
      const generalDefaultData = getGeneralInformationModels(this.categorisedSchemaGeo).reduce(
        (acc, model) => ({ ...acc, [model]: this.updatedEntity.entity.default[model] }),
        {}
      )

      this.$set(this.updatedEntity.entity, [country], generalDefaultData)
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    onDeleteConfirm() {
      this.deleteEntity()
      this.showDeleteModal = false
    },
    onDeleteCountry(country) {
      this.tabGeo = 0
      this.$delete(this.updatedEntity.entity, country)
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.updatedEntity[model]) {
          this.$set(this.updatedEntity[model], context, value)
        } else {
          this.$set(this.updatedEntity, model, { [context]: value })
        }
      } else this.$set(this.updatedEntity, model, value)
    },
    onInputGeo({ context, model, value }) {
      if (context) {
        if (this.updatedEntityByGeo[model]) {
          this.$set(this.updatedEntityByGeo[model], context, value)
        } else {
          this.$set(this.updatedEntityByGeo, model, { [context]: value })
        }
      } else this.$set(this.updatedEntityByGeo, model, value)
    },
    onResetConfirm() {
      this.syncUpdatedEntity()
      this.showResetModal = false
      this.$notify({
        type: 'success',
        text: 'Entity data reset successfully.',
      })
    },
    onSaveConfirm() {
      this.updateEntity()
      this.showSaveModal = false
    },
    syncUpdatedEntity() {
      this.updatedEntity = cloneDeep(this.entity)
    },
    async updateEntity() {
      this.isLoading = true

      try {
        const clonedData = cloneDeep(this.updatedEntity)
        const spreadData = merge(clonedData, clonedData.entity)
        await entities.updateEntity(this.$route.params.id, spreadData)
        await this.getEntity()
        this.syncUpdatedEntity()
        this.$notify({
          type: 'success',
          text: `Entity saved successfully`,
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to save entity',
        })
      }

      this.isLoading = false
    },
  },
}
</script>
